import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { UpdateBrand } from "../../../redux/services/BrandService/brand.Services";
import { ActiveCategoryList } from "../../../redux/services/categoriesService/categories.Services";
import { ActiveChildCategoryList } from "../../../redux/services/categoriesService/categories.Services";
import { ActiveUserList } from "../../../redux/services/User Services/User.Services";
import { ActiveSubCategoryList } from "../../../redux/services/subCategoriesService/subCategories.Services";
import { successToast, errorToast } from "../../../Utils/toast";
import { closeProductModal, refreshProductList } from "../../../redux/features/productModalSlice";

function EditBrand({ data, onSuccess }) {
  const dispatch = useDispatch();
  const [preview, setPreview] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);

  const [formData, setFormData] = useState({
    vendor_id: "",
    category_id: "",
    sub_category_id: "",
    child_category_id: "",
    name: "",
    status: true,
    logo: null,
  });

  // Populate form when data arrives + load dependent lists
  useEffect(() => {
    if (data) {
      setFormData({
        vendor_id: data.vendor_id || data.user_id || "",
        category_id: data.category_id || "",
        sub_category_id: data.sub_category_id || "",
        child_category_id: data.child_category_id || "",
        name: data.name || "",
        status: data.status === 1 || data.status === true,
        logo: null,
      });

      if (data.logo) {
        setPreview(`${process.env.REACT_APP_IMAGE_URL}/brand/${data.logo}`);
      }

      // Re-hydrate cascading dropdowns
      if (data.category_id) fetchSubCategories(data.category_id);
      if (data.sub_category_id) fetchChildCategories(data.sub_category_id);
    }
  }, [data]);

  const fetchCategories = async () => {
    try {
      const res = await ActiveCategoryList();
      if (res?.success) setCategories(res.data || []);
    } catch (error) {
      console.log(error);
      errorToast("Something went wrong");
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const res = await ActiveSubCategoryList({ category_id: categoryId });
      if (res?.data?.success) {
        setSubCategories(res.data.data || []);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.log(error);
      errorToast("Something went wrong");
    }
  };

  const fetchChildCategories = async (subCategoryId) => {
    try {
      const res = await ActiveChildCategoryList({ sub_category_id: subCategoryId });
      if (res?.data?.success) {
        setChildCategories(res.data.data || []);
      } else {
        setChildCategories([]);
      }
    } catch (error) {
      console.log(error);
      errorToast("Something went wrong");
    }
  };

  // Load categories + users on mount
  useEffect(() => {
    fetchCategories();

    const fetchUser = async () => {
      try {
        const res = await ActiveUserList();
        if (res?.success) {
          setActiveUsers(res?.data || []);
        } else {
          errorToast(res?.message || "Failed to fetch users");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        errorToast(error?.response?.data?.message || "Something went wrong while fetching users");
      }
    };
    fetchUser();
  }, []);

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "logo") {
      const file = files[0];
      setFormData((prev) => ({ ...prev, logo: file }));
      if (file) setPreview(URL.createObjectURL(file));
      return;
    }

    if (name === "status") {
      setFormData((prev) => ({ ...prev, status: value === "Active" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" && { sub_category_id: "", child_category_id: "" }),
      ...(name === "sub_category_id" && { child_category_id: "" }),
    }));

    if (name === "category_id") {
      setChildCategories([]);
      if (value) {
        await fetchSubCategories(value);
      } else {
        setSubCategories([]);
      }
    }

    if (name === "sub_category_id") {
      if (value) {
        await fetchChildCategories(value);
      } else {
        setChildCategories([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const payload = new FormData();
      payload.append("vendor_id", formData.vendor_id);
      payload.append("category_id", formData.category_id);
      payload.append("sub_category_id", formData.sub_category_id);
      payload.append("child_category_id", formData.child_category_id);
      payload.append("id", data.id);
      payload.append("name", formData.name);
      payload.append("status", formData.status ? 1 : 0);

      if (formData.logo) payload.append("logo", formData.logo);

      const res = await UpdateBrand(payload);
      successToast(res?.message);
      dispatch(refreshProductList());
      dispatch(closeProductModal());
      if (onSuccess) onSuccess();
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || "Error updating brand");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-form-wrapper">
      <form onSubmit={handleSubmit} className="row g-3">
        {/* Vendor */}
        <div className="col-md-6">
          <label>Vendor</label>
          <select
            className="form-control"
            name="vendor_id"
            value={formData.vendor_id}
            onChange={handleChange}
          >
            <option value="">Select Vendor</option>
            {activeUsers.map((item) => (
              <option key={item.id} value={item.id}>{item.name||item.mobile}</option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="col-md-6">
          <label>Category</label>
          <select
            className="form-control"
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
          >
            <option value="">Select Category</option>
            {categories.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>

        {/* Sub Category */}
        <div className="col-md-6">
          <label>Sub Category</label>
          <select
            className="form-control"
            name="sub_category_id"
            value={formData.sub_category_id}
            onChange={handleChange}
            disabled={!formData.category_id}
          >
            <option value="">Select Sub Category</option>
            {subCategories.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>

        {/* Child Category */}
        <div className="col-md-6">
          <label>Child Category</label>
          <select
            className="form-control"
            name="child_category_id"
            value={formData.child_category_id}
            onChange={handleChange}
            disabled={!formData.sub_category_id}
          >
            <option value="">Select Child Category</option>
            {childCategories.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>

        {/* Brand Name */}
        <div className="col-md-6">
          <label>Brand Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-control"
            placeholder="Enter Brand name"
          />
        </div>

        {/* Logo */}
        <div className="col-md-6 mb-3">
          <label>Brand Logo</label>
          <input
            type="file"
            name="logo"
            className="form-control"
            onChange={handleChange}
            accept="image/*"
          />
          <div className="text-center mt-3">
            <img
              src={preview || "/placeholder-image.png"}
              alt="Brand Preview"
              width="120"
              height="120"
              className="img-thumbnail"
              style={{ objectFit: "cover" }}
            />
          </div>
        </div>

        {/* Status */}
        <div className="col-md-6">
          <label>Status</label>
          <select
            name="status"
            value={formData.status ? "Active" : "Inactive"}
            onChange={handleChange}
            className="form-control"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="col-md-12 text-end">
          <button type="submit" disabled={isLoading} className="add-btn">
            {isLoading ? "Updating..." : "Update Brand"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditBrand;